import { useEffect } from 'react';
import { queryMatches, type ServerScreen } from './serverWindow';

// The page's first drawing in the browser must match the one the server sent, or React throws the server's away and
// draws again. The server drew for the screen a browser's name suggests — an iPad and a narrow desktop window are
// named as computers — and marked the root with it; until the page is taken over, a media query is answered for that
// screen, and after it, for the real window. The marks on the root are the only state, and they go once it is done.

/** The marks on the root naming the screen the server drew for. */
const MARKS = ['data-drawn-width', 'data-drawn-height', 'data-drawn-touch'] as const;

// Reads the screen the server drew this page for off the root's marks, or nothing once they are gone or never were.
function drawnScreen(): ServerScreen | null {
  const root = typeof document === 'undefined' ? null : document.getElementById('root');
  const width = Number(root?.getAttribute('data-drawn-width'));
  if (!root || !width) return null;
  return { width, height: Number(root.getAttribute('data-drawn-height')) || 0, touch: root.getAttribute('data-drawn-touch') === '1' };
}

// Answers a media query for a drawing's first state: the screen the server drew for while the page is taken over,
// else the window itself. Read it only to start state; an effect re-reads the window once the page is live.
export function firstMatch(query: string): boolean {
  const screen = drawnScreen();
  return screen ? queryMatches(query, screen) : window.matchMedia(query).matches;
}

// Takes the root's marks off once the page has been taken over, so whatever draws next answers for the real window.
export function useDrawnScreenDone(): void {
  useEffect(() => {
    const root = document.getElementById('root');
    for (const mark of MARKS) root?.removeAttribute(mark);
  }, []);
}
